import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { TASK_TYPE_LABELS } from '@claimup/shared/constants/assignments';
import MainLayout from '../../layouts/MainLayout';
import userStore from '../../store/userStore';
import api, { getErrorMessage } from '../../services/api';
import { translateInteractionType } from '../ManagementListPage/components/InteractionItem/utils/translateInteractionType';
import PaymentPlansList from '../../components/PaymentPlans/PaymentPlansList';

/**
 * «Cobros parciales»: la fase 0 (solo admin, solo lectura). Los
 * expedientes en «Plan de pagos» con su ritmo de pago. Plan:
 * docs/planes-de-pago-plan.md.
 *
 * 🔴 No hay cuotas guardadas: el semáforo compara los días desde el último pago
 * con la cadencia habitual del deudor. La pantalla lo dice siempre, no en un
 * tooltip, para que nadie lea «al día» como «cumple lo pactado».
 *
 * La tabla y la cabecera se piden por separado: si una falla, la otra se ve.
 */

const nf = new Intl.NumberFormat('es-ES');
const nf2 = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const eur = v => (v == null ? '—' : `${nf2.format(v)} €`);
const fecha = d => (d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');

export const LIGHTS = {
  stalled: { label: 'Parado', dot: 'bg-rose-500', text: 'text-rose-700' },
  late: { label: 'Atrasado', dot: 'bg-amber-400', text: 'text-amber-700' },
  on_track: { label: 'Al día', dot: 'bg-emerald-500', text: 'text-emerald-700' },
};

const SET_BY = { ai: 'IA', user: 'Persona', system: 'Al registrar un pago' };

const TODOS = 'all';

/** El estado de los filtros → los parámetros de la petición. Sin valores vacíos. */
export const paramsFor = ({ client, includeCourt, light, withoutTask, hideSingle }, forList = true) => {
  const p = {};
  if (client && client !== TODOS) p.clients = client;
  if (includeCourt) p.include_court = 'true';
  if (!forList) return p;
  if (light && light !== TODOS) p.lights = light;
  if (withoutTask) p.without_task = 'true';
  if (hideSingle) p.hide_single = 'true';
  return p;
};

const Tile = ({ label, value, sub, tone = 'text-slate-800' }) => (
  <div className='bg-white border border-slate-200 rounded-lg px-4 py-3'>
    <div className='text-xs text-slate-500'>{label}</div>
    <div className={`text-xl font-semibold mt-1 ${tone}`}>{value}</div>
    {sub && <div className='text-xs text-slate-500 mt-0.5'>{sub}</div>}
  </div>
);

const Cabecera = ({ estado, data }) => {
  if (estado === 'error') {
    return (
      <div className='bg-white border border-rose-200 rounded-lg px-4 py-3 text-sm text-rose-700'>
        No se han podido cargar las cifras de cabecera.
      </div>
    );
  }
  if (estado !== 'ok') {
    return <div className='bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-400'>Cargando cifras…</div>;
  }
  return (
    <div className='grid grid-cols-2 lg:grid-cols-4 gap-3'>
      <Tile label='Expedientes en plan' value={nf.format(data.total.expedients)} sub={`Cobrado: ${eur(data.collected)}`} />
      <Tile label='Deuda viva' value={eur(data.total.importe_deuda)} />
      <Tile
        label='Parados'
        value={nf.format(data.by_light.stalled.expedients)}
        sub={eur(data.by_light.stalled.importe_deuda)}
        tone='text-rose-700'
      />
      <Tile
        label='Sin tarea pendiente'
        value={nf.format(data.without_task.expedients)}
        sub={eur(data.without_task.importe_deuda)}
        tone={data.without_task.expedients ? 'text-amber-700' : 'text-slate-800'}
      />
    </div>
  );
};

const Ritmo = ({ r }) => {
  if (!r.payments_count) return <span className='text-slate-500'>Sin pagos</span>;
  return (
    <div>
      <div>
        {r.delay_days > 0 ? `${r.delay_days} d tarde` : 'Dentro de su ritmo'}
      </div>
      <div className='text-xs text-slate-500'>
        {r.cadence_estimated ? `Pago suelto · se suponen ${r.cadence_days} d` : `Paga cada ~${r.cadence_days} d`}
      </div>
    </div>
  );
};

const Fila = ({ r }) => {
  const light = LIGHTS[r.light];
  return (
    <tr className='border-t border-slate-100 align-top'>
      <td className='px-3 py-2'>
        <span className={`inline-flex items-center gap-1.5 font-medium ${light.text}`}>
          <span className={`w-2 h-2 rounded-full ${light.dot}`} aria-hidden='true' />
          {light.label}
        </span>
      </td>
      <td className='px-3 py-2'>
        <Link to={`/view/${r._id}`} className='text-indigo-600 hover:underline font-medium'>
          {r.debtor_name || 'Sin nombre'}
        </Link>
        <div className='text-xs text-slate-500'>
          {[r.client_name, r.nombre_comercial].filter(Boolean).join(' · ') || '—'}
        </div>
      </td>
      <td className='px-3 py-2 text-right whitespace-nowrap'>{eur(r.importe_deuda)}</td>
      <td className='px-3 py-2 text-right whitespace-nowrap'>
        {eur(r.collected)}
        <div className='text-xs text-slate-500'>
          {r.payments_count} {r.payments_count === 1 ? 'pago' : 'pagos'}
        </div>
      </td>
      <td className='px-3 py-2 whitespace-nowrap'>
        {r.last_payment ? (
          <>
            {fecha(r.last_payment.date)}
            <div className='text-xs text-slate-500'>{eur(r.last_payment.amount)}</div>
          </>
        ) : (
          '—'
        )}
      </td>
      <td className='px-3 py-2 whitespace-nowrap'>
        <Ritmo r={r} />
      </td>
      <td className='px-3 py-2'>
        {r.pending_task ? (
          <>
            {TASK_TYPE_LABELS[r.pending_task.task_type] || r.pending_task.task_type}
            <div className='text-xs text-slate-500'>{fecha(r.pending_task.date)}</div>
          </>
        ) : (
          <span className='text-amber-700'>Ninguna</span>
        )}
      </td>
      <td className='px-3 py-2'>
        {r.last_contact ? (
          <>
            {translateInteractionType(r.last_contact.type)}
            <div className='text-xs text-slate-500'>{fecha(r.last_contact.date)}</div>
          </>
        ) : (
          '—'
        )}
      </td>
      <td className='px-3 py-2 whitespace-nowrap'>
        {r.set_by ? (
          <>
            {SET_BY[r.set_by] || r.set_by}
            <div className='text-xs text-slate-500'>{fecha(r.in_status_since)}</div>
          </>
        ) : (
          <span className='text-slate-400'>No consta</span>
        )}
      </td>
    </tr>
  );
};

const Check = ({ label, checked, onChange }) => (
  <label className='inline-flex items-center gap-2 text-sm text-slate-600'>
    <input type='checkbox' className='form-checkbox rounded-lg' checked={checked} onChange={e => onChange(e.target.checked)} />
    {label}
  </label>
);

const CobrosParciales = () => {
  const user = userStore(state => state.user);
  const esAdmin = user?.role === 'admin';

  const [filtros, setFiltros] = useState({
    client: TODOS,
    includeCourt: false,
    light: TODOS,
    withoutTask: false,
    hideSingle: false,
  });
  const [clientes, setClientes] = useState([]);
  const [tabla, setTabla] = useState({ estado: 'loading', data: [] });
  const [cabecera, setCabecera] = useState({ estado: 'loading', data: null });

  const set = key => value => setFiltros(f => ({ ...f, [key]: value }));

  const listKey = useMemo(() => JSON.stringify(paramsFor(filtros)), [filtros]);
  const scopeKey = useMemo(() => JSON.stringify(paramsFor(filtros, false)), [filtros]);

  useEffect(() => {
    if (!esAdmin) return;
    api
      .get('/payment-plans/clients', { params: filtros.includeCourt ? { include_court: 'true' } : {} })
      .then(({ data }) => setClientes(data.data || []))
      .catch(err => toast.error(getErrorMessage(err)));
  }, [esAdmin, filtros.includeCourt]);

  useEffect(() => {
    if (!esAdmin) return undefined;
    let vigente = true;
    setTabla(t => ({ ...t, estado: 'loading' }));
    api
      .get('/payment-plans', { params: JSON.parse(listKey) })
      .then(({ data }) => vigente && setTabla({ estado: 'ok', data: data.data || [] }))
      .catch(() => vigente && setTabla({ estado: 'error', data: [] }));
    return () => {
      vigente = false;
    };
  }, [esAdmin, listKey]);

  useEffect(() => {
    if (!esAdmin) return undefined;
    let vigente = true;
    setCabecera(c => ({ ...c, estado: 'loading' }));
    api
      .get('/payment-plans/summary', { params: JSON.parse(scopeKey) })
      .then(({ data }) => vigente && setCabecera({ estado: 'ok', data: data.data }))
      .catch(() => vigente && setCabecera({ estado: 'error', data: null }));
    return () => {
      vigente = false;
    };
  }, [esAdmin, scopeKey]);

  if (!esAdmin) return null;

  return (
    <>
      <div className='space-y-4'>
        <header className='bg-white border border-slate-200 rounded-lg px-5 py-4 flex flex-wrap items-end justify-between gap-3'>
          <div>
            <h2 className='font-semibold text-slate-800 text-lg'>Cobros parciales</h2>
            <p className='text-sm text-slate-500 mt-1'>Los expedientes en «Plan de pagos» o en «Cobro parcial extrajudicial», y si siguen pagando.</p>
          </div>
          <label className='text-sm text-slate-600 flex items-center gap-2'>
            Cliente
            <select
              aria-label='Cliente'
              value={filtros.client}
              onChange={e => set('client')(e.target.value)}
              className='form-select rounded-lg border-slate-300 text-sm min-w-[16rem]'
            >
              <option value={TODOS}>Todos</option>
              {clientes.map(c => (
                <option key={c.user_id || 'sin'} value={c.user_id || ''} disabled={!c.user_id}>
                  {`${c.name} (${nf.format(c.expedients)})`}
                </option>
              ))}
            </select>
          </label>
        </header>

        <div className='flex gap-2 items-start bg-sky-50 border border-sky-200 rounded-lg px-4 py-3 text-sm text-sky-800'>
          <InformationCircleIcon className='w-5 h-5 shrink-0 mt-0.5' />
          <p>
            <strong>El ritmo es una estimación.</strong> Todavía no se guardan las cuotas acordadas: el semáforo compara los
            días desde el último pago con lo que suele tardar ese deudor entre pagos. Con un solo pago se suponen 30 días.
            Los cobros apuntados solo en una anotación no cuentan.
          </p>
        </div>

        <Cabecera estado={cabecera.estado} data={cabecera.data} />

        <div className='bg-white border border-slate-200 rounded-lg'>
          <div className='flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 border-b border-slate-200'>
            <label className='text-sm text-slate-600 flex items-center gap-2'>
              Semáforo
              <select
                aria-label='Semáforo'
                value={filtros.light}
                onChange={e => set('light')(e.target.value)}
                className='form-select rounded-lg border-slate-300 text-sm'
              >
                <option value={TODOS}>Todos</option>
                {Object.entries(LIGHTS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.label}
                  </option>
                ))}
              </select>
            </label>
            <Check label='Solo sin tarea pendiente' checked={filtros.withoutTask} onChange={set('withoutTask')} />
            <Check label='Ocultar pagos sueltos' checked={filtros.hideSingle} onChange={set('hideSingle')} />
            <Check label='Incluir cobro parcial judicial' checked={filtros.includeCourt} onChange={set('includeCourt')} />
          </div>

          {tabla.estado === 'error' && (
            <p className='px-4 py-6 text-sm text-rose-700'>No se ha podido cargar la lista.</p>
          )}
          {tabla.estado === 'loading' && <p className='px-4 py-6 text-sm text-slate-400'>Cargando…</p>}
          {tabla.estado === 'ok' && tabla.data.length === 0 && (
            <p className='px-4 py-6 text-sm text-slate-500'>Ninguno con estos filtros.</p>
          )}
          {tabla.estado === 'ok' && tabla.data.length > 0 && (
            <div className='overflow-x-auto'>
              <table className='min-w-full text-sm text-slate-700'>
                <thead className='text-xs text-slate-500 bg-slate-50'>
                  <tr>
                    <th className='px-3 py-2 text-left font-medium'>Estado</th>
                    <th className='px-3 py-2 text-left font-medium'>Deudor</th>
                    <th className='px-3 py-2 text-right font-medium'>Deuda viva</th>
                    <th className='px-3 py-2 text-right font-medium'>Cobrado</th>
                    <th className='px-3 py-2 text-left font-medium'>Último pago</th>
                    <th className='px-3 py-2 text-left font-medium'>Ritmo</th>
                    <th className='px-3 py-2 text-left font-medium'>Próxima tarea</th>
                    <th className='px-3 py-2 text-left font-medium'>Último contacto</th>
                    <th className='px-3 py-2 text-left font-medium'>Puesto en plan por</th>
                  </tr>
                </thead>
                <tbody>
                  {tabla.data.map(r => (
                    <Fila key={r._id} r={r} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};


/**
 * «Planes de pago» (docs/planes-de-pago-fase-1-plan.md, §7).
 *
 *   · admin: dos pestañas. «Planes», los acordados con cuotas, y «Cobros
 *     parciales», la vista de la fase 0 con el ritmo estimado, que es de donde
 *     salen los planes nuevos.
 *   · empresa con AR, gestor y operator: solo los planes de su cartera. Si su
 *     empresa no les ha dado acceso, el servidor dice 403 y se enseña el motivo.
 */
const TABS = [
  { id: 'plans', label: 'Planes' },
  { id: 'partials', label: 'Cobros parciales' },
];

const PaymentPlansPage = () => {
  const user = userStore(state => state.user);
  const esAdmin = user?.role === 'admin';
  const [tab, setTab] = useState('plans');

  return (
    <MainLayout>
      <div className='space-y-4'>
        <header className='bg-white border border-slate-200 rounded-lg px-5 py-4'>
          <h1 className='text-2xl font-semibold text-slate-900'>Planes de pago</h1>
          <p className='text-sm text-slate-500 mt-1'>
            Los planes acordados con los deudores: sus cuotas, lo pagado y lo que va con retraso.
          </p>
          {esAdmin && (
            <div className='flex gap-2 mt-3' role='tablist'>
              {TABS.map(t => (
                <button
                  key={t.id}
                  type='button'
                  role='tab'
                  aria-selected={tab === t.id}
                  onClick={() => setTab(t.id)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                    tab === t.id ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </header>
        {esAdmin && tab === 'partials' ? <CobrosParciales /> : <PaymentPlansList />}
      </div>
    </MainLayout>
  );
};

export default PaymentPlansPage;
